import React, { useState, useEffect } from 'react';
import axios from 'axios';
import urlList from '../../reducers/propertiesReducer';
import getAxiosConfig from '../../utils/axiosConfig';
import DeleteSegmentation from './DeleteSegmentation';
import ContentWithTopLabel from "../NodeEditorPanel/components/ContentWithTopLabel";

const SegmentationForm = ({
  onCancel,
  segmentationId,
  segmentationType,
  groupName,
  onSegmentationChange
}) => {
    const [isCreating, setIsCreating] = useState(true);
    const [selectedSegmentationType, setSelectedSegmentationType] = useState('manual');
    const [segmentationName, setSegmentationName] = useState('');
    const [conditions, setConditions] = useState([{ name: '', filterRule: '' }]);
    const [segmentProcessors, setSegmentProcessors] = useState([]);
    const [selectedProcessor, setSelectedProcessor] = useState(null);
    const [isVisible, setIsVisible] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        setIsVisible(true);
        fetchSegmentProcessors().then((segmentProcessors) => {
            if (segmentationId) {
                setIsCreating(false);
                fetchSegmentationDetails(segmentationId, segmentProcessors);
            }
        });
    }, [groupName, segmentationId]);

    const fetchSegmentProcessors = async () => {
        try {
            const response = await axios.get(
                `${urlList().segmentationUrl}/getSegmentationProcessors`,
                getAxiosConfig()
            );
            setSegmentProcessors(response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching segmentation processors:', error);
        }
    };

    const fetchSegmentationDetails = async (segmentationId, segmentProcessors) => {
        try {
            const response = await axios.get(
                `${urlList().segmentationUrl}/getSegmentationDefinition/${segmentationId}_${segmentationType}`,
                getAxiosConfig()
            );

            const [firstSegmentDetail] = response.data;

            if (firstSegmentDetail) {
                setSelectedSegmentationType(firstSegmentDetail.type);

                if (firstSegmentDetail.type === 'manual') {
                    setSegmentationName(groupName);
                    setConditions(
                        response.data.map((segment) => ({
                            name: segment.segmentName,
                            filterRule: segment.spelfilterString
                        }))
                    );
                } else if (firstSegmentDetail.type === 'automatic') {
                    setSegmentationName(groupName);
                    const processor = segmentProcessors.find(
                        (processor) =>
                            processor === firstSegmentDetail.segmentProcessor
                    );
                    setSelectedProcessor(processor);
                }
            }
        } catch (error) {
            console.error('Error fetching segmentation details for updating:', error);
        }
    };

    const handleCancel = () => {
            setIsVisible(false);
            onCancel();

    };

    const handleAddCondition = () => {
        setConditions([...conditions, { name: '', filterRule: '' }]);
    };

    const handleRemoveCondition = (index) => {
        setConditions((prevConditions) =>
            prevConditions.filter((_, i) => i !== index)
        );
    };

    const validateForm = () => {
        if (selectedSegmentationType === 'manual' && !segmentationName && isCreating) {
            setErrorMessage('Please enter a group name.');
            return false;
        }

        if (
            selectedSegmentationType === 'manual' &&
            conditions.some((condition) => !condition.name || !condition.filterRule)
        ) {
            setErrorMessage('Please fill in all segmentation name and filter rule fields.');
            return false;
        }

        if (selectedSegmentationType === 'automatic' && !selectedProcessor) {
            setErrorMessage('Please select a segmentation processor.');
            return false;
        }

        return true;
    };

    const handleSaveSegmentation = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            let requestData;

            if (selectedSegmentationType === 'manual') {
                requestData = {
                    groupName: segmentationName,
                    segments: conditions.map(({ name, filterRule }) => ({
                        segmentationName: name,
                        spelFilter: filterRule
                    }))
                };

                const url = isCreating
                    ? `${urlList().segmentationUrl}/addManualSegmentationDefinition`
                    : `${urlList().segmentationUrl}/updateManualSegmentationDefinition/${segmentationType}/${segmentationId}`;

                await axios.post(url, requestData, getAxiosConfig());
            } else if (selectedSegmentationType === 'automatic' && selectedProcessor) {
                requestData = {
                    groupName: segmentationName,
                    segmentationProcessor: selectedProcessor,
                    options: {
                        segmentPrefix: "test"
                    }
                };

                const url = isCreating
                    ? `${urlList().segmentationUrl}/addAutomaticSegmentationDefinition`
                    : `${urlList().segmentationUrl}/updateAutomaticSegmentationDefinition/${segmentationType}/${segmentationId}`;

                await axios.post(url, requestData, getAxiosConfig());
            }

            console.log('Segmentation saved successfully');
            handleCancel();
            onSegmentationChange();
        } catch (error) {
            console.error('Error saving/updating segmentation:', error);
        }
    };

    return (
        <div className="creation-segmentation-screen">
            <div className={`creation-segmentation-form ${isVisible ? 'fadeIn' : 'fadeOut'}`}>
                <div>
          <span className="creation-segmentation-close" onClick={onCancel}>
            &times;
          </span>
                    { (isCreating || (selectedSegmentationType === 'automatic' && !segmentationId)) && (
                        <label>
                            Group name:
                            <input type="text" value={segmentationName} onChange={(e) => setSegmentationName(e.target.value)} />
                        </label>
                    )}
                    <label>
                        Segmentation type:
                        <select value={selectedSegmentationType} onChange={(e) => setSelectedSegmentationType(e.target.value)}>
                            <option value="manual">Manual</option>
                            <option value="automatic">Automatic</option>
                        </select>
                    </label>
                    {selectedSegmentationType === 'manual' && (
                        <div>
                            {conditions.map((condition, index) => (
                                    <div key={index}>
                                        <ContentWithTopLabel label="Segment">
                                            <label>
                                                Segment name:
                                                <input
                                                    type="text"
                                                    value={condition.name}
                                                    onChange={(e) => setConditions((prevConditions) =>
                                                        prevConditions.map((prevCondition, i) =>
                                                            i === index ? { ...prevCondition, name: e.target.value } : prevCondition
                                                        ))}
                                                />
                                            </label>
                                            <label>
                                                Filter rule:
                                                <input
                                                    type="text"
                                                    value={condition.filterRule}
                                                    onChange={(e) => setConditions((prevConditions) =>
                                                        prevConditions.map((prevCondition, i) =>
                                                            i === index ? { ...prevCondition, filterRule: e.target.value } : prevCondition
                                                        ))}
                                                />
                                            </label>
                                            {index !== 0 && (
                                                <button onClick={() => handleRemoveCondition(index)}>Remove</button>
                                            )}
                                        </ContentWithTopLabel>
                                    </div>
                            ))}
                            <button onClick={handleAddCondition}>Add Segment</button>
                        </div>
                    )}
                    {selectedSegmentationType === 'automatic' && (
                        <div>
                            <label>
                                Select Segmentation Processor:
                                <select
                                    value={selectedProcessor ? selectedProcessor : ''}
                                    onChange={(e) => {
                                        const selectedProcessorObj = segmentProcessors.find(
                                            (processor) => processor === e.target.value
                                        );
                                        setSelectedProcessor(selectedProcessorObj);
                                    }}
                                >
                                    <option value="" disabled>
                                        Select Segmentation Processor
                                    </option>
                                    {segmentProcessors.map((processor, index) => (
                                        <option key={index} value={processor}>
                                            {processor}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    )}
                    {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                    <button onClick={handleSaveSegmentation}>
                        {isCreating ? 'Create Segmentation' : 'Update Segmentation'}
                    </button>
                    {!isCreating && <button onClick={handleCancel}>Cancel</button>}
                    {!isCreating && <DeleteSegmentation segmentationId={segmentationId} type={segmentationType} onDelete={handleCancel} />}
                </div>
            </div>
        </div>
    );
};

export default SegmentationForm;









